import React, { useState, useRef, useEffect } from 'react';
import { styled } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import MuiAccordion from '@mui/material/Accordion';
import MuiAccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AddIcon from '@mui/icons-material/AddCircleOutlineOutlined';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import TextInput from 'components/form/TextInput';
import DateInput from 'components/form/DatePicker';
import Button from 'components/Button';
import Form from 'components/form';
import { useForm } from './Form';
const { FormItem } = Form;

const Accordion = styled((props) => (
  <MuiAccordion disableGutters elevation={0} square {...props} />
))(({ theme }) => ({
  'marginBottom': theme.spacing(1),
  'border': `1px solid ${theme.palette.divider}`,
  'borderRadius': theme.shape.borderRadius,
  '&:before': {
    display: 'none',
  },
}));

const AccordionSummary = styled((props) => (
  <MuiAccordionSummary expandIcon={<ExpandMoreIcon />} {...props} />
))(({ theme }) => ({
  '& .MuiAccordionSummary-content': {
    marginLeft: theme.spacing(1),
  },
}));

const validator = (rule, value, callback) => {
  if (rule.isRequired) {
    if (!value.length) {
      callback(rule.message);
    } else {
      const hasEmpty = value.some(
        (item) =>
          !item.firstName || !item.lastName || !item.dateOfBirth || !item.notes,
      );
      if (hasEmpty) {
        callback(rule.message);
      } else {
        callback();
      }
    }
  } else {
    callback();
  }
};

const Guests = React.forwardRef(
  ({ value, onChange, hasError, helpText }, ref) => {
    const [expanded, setExpanded] = useState(0);

    const onAddItem = () => {
      value.push({
        firstName: '',
        lastName: '',
        dateOfBirth: '',
        notes: '',
      });
      onChange(value);
      const index = value.length - 1;
      setExpanded(index);
    };

    const onRemoveItem = (index) => {
      value.splice(index, 1);
      onChange(value);
    };

    const handleChange = (guest, name, v) => {
      guest[name] = v;
      onChange(value);
    };

    return (
      <Box>
        {value?.map((guest, index) => {
          return (
            <Accordion
              id={`guest-form-${index}`}
              expanded={expanded === index}
              onChange={() => setExpanded(index)}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography fontWeight={500}>
                  {guest.firstName && guest.lastName
                    ? `${guest.firstName} ${guest.lastName}`
                    : `Member ${index + 1}`}
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Grid container spacing={1}>
                  <Grid item xs={6}>
                    <TextInput
                      fullWidth
                      value={guest.firstName}
                      onChange={(e) =>
                        handleChange(guest, 'firstName', e.target.value)
                      }
                      margin="none"
                      placeholder="First Name"
                      hasError={hasError && !guest.firstName}
                      helpText={helpText}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextInput
                      fullWidth
                      value={guest.lastName}
                      onChange={(e) =>
                        handleChange(guest, 'lastName', e.target.value)
                      }
                      placeholder="Last Name"
                      margin="none"
                      hasError={hasError && !guest.lastName}
                      helpText={helpText}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <DateInput
                      fullWidth
                      value={guest.dateOfBirth}
                      onChange={(value) =>
                        handleChange(guest, 'dateOfBirth', value)
                      }
                      disableFuture
                      placeholder="Date of Birth"
                      margin="none"
                      hasError={hasError && !guest.dateOfBirth}
                      helpText={helpText}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextInput
                      fullWidth
                      value={guest.notes}
                      onChange={(e) =>
                        handleChange(guest, 'notes', e.target.value)
                      }
                      margin="none"
                      label="Special Instructions"
                      placeholder="eg: needs supervision, is very shy, doesn’t do well in big crowds, etc."
                      hasError={hasError && !guest.notes}
                      helpText={helpText}
                    />
                    <Button
                      sx={{ mt: 1 }}
                      startIcon={<DeleteIcon fontSize="inherit" />}
                      size="small"
                      variant="text"
                      color="secondary"
                      onClick={() => onRemoveItem(index)}
                    >
                      Remove
                    </Button>
                  </Grid>
                </Grid>
              </AccordionDetails>
            </Accordion>
          );
        })}
        <Box textAlign="center">
          <Button
            startIcon={<AddIcon fontSize="inherit" />}
            variant="outlined"
            onClick={onAddItem}
          >
            Add Another Child
          </Button>
        </Box>
      </Box>
    );
  },
);

const GuestsForm = ({ id, required }) => {
  const { getFieldDecorator } = useForm();
  return (
    <FormItem>
      {getFieldDecorator(id, {
        initialValue: [
          { firstName: '', lastName: '', dateOfBirth: '', notes: '' },
        ],
        rules: [{ validator, message: 'Required', isRequired: required }],
      })(<Guests />)}
    </FormItem>
  );
};
export default GuestsForm;
